import React from 'react';
import { Key, Users, UserCheck, Fingerprint, EyeOff, ShieldCheck } from 'lucide-react';
import './SecurityCompliance.css';

const AccessControl = () => {
  return (
    <div className="security-page">
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-copy">
            <div className="status-badge">
              <Fingerprint size={14} /> Biometric & MFA Enabled
            </div>
            <h1>Granular Identity & Access Management</h1>
            <p>
              Ensure the right people have the right access at the right time. 
              Secure your infrastructure with modern zero-trust architecture.
            </p>
            <div className="hero-actions">
              <a className="button primary" href="#contact">Audit Access</a>
              <a className="button secondary" href="#iam">Explore IAM</a>
            </div>
          </div>
          <div className="hero-panel">
            <div className="panel-card panel-gradient">
              <Key className="icon" size={48} />
              <h3>Zero Trust</h3>
              <p>Never trust, always verify. Every request is authenticated and authorized.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="iam" className="section">
        <div className="section-heading">
          <span className="eyebrow">Identity First</span>
          <h2>Control without friction.</h2>
          <p>We implement Single Sign-On (SSO) and Role-Based Access Control (RBAC) to simplify user management while maximizing security.</p>
        </div>

        <div className="security-grid">
          <div className="security-card">
            <div className="sec-icon"><Users size={24} /></div>
            <h3>SSO Integration</h3>
            <p>Connect with Google Workspace, Microsoft Entra ID (Azure AD), or Okta for unified login.</p>
          </div>
          <div className="security-card">
            <div className="sec-icon"><UserCheck size={24} /></div>
            <h3>RBAC Polices</h3>
            <p>Define precise permissions based on job roles to prevent broad internal data access.</p>
          </div>
          <div className="security-card">
            <div className="sec-icon"><EyeOff size={24} /></div>
            <h3>MFA Enforcement</h3>
            <p>Mandatory Multi-Factor Authentication via TOTP, SMS, or FIDO2 hardware keys.</p>
          </div>
        </div>
      </section>

      <section className="section bg-glass">
        <div className="feature-block">
          <div className="encryption-visual">
             <Fingerprint className="lock-animation" size={100} color="#10b981" />
          </div>
          <div className="solution-copy">
            <h3>Just-In-Time (JIT) Access</h3>
            <p>
              Eliminate permanent "god-mode" accounts. Grant temporary high-level 
              permissions only when maintenance is required, then auto-revoke.
            </p>
            <ul className="feature-list-alt">
              <li className="feature-item-alt">
                <ShieldCheck size={20} className="sec-icon-inline" />
                <div>
                  <h4>Audit Logging</h4>
                  <p>Every login attempt and permission change is logged permanently for compliance.</p>
                </div>
              </li>
              <li className="feature-item-alt">
                <ShieldCheck size={20} className="sec-icon-inline" />
                <div>
                  <h4>Contextual Auth</h4>
                  <p>Challenge logins from new locations or devices with additional security layers.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Managing too many passwords?</h2>
          <p>Upgrade to an enterprise Identity provider and secure your team's access today.</p>
          <a className="button primary" href="#contact">Build My IAM Strategy</a>
        </div>
      </section>
    </div>
  );
};

export default AccessControl;
